export const normalizeWhitespace = (value = "") => value.replace(/\s+/g, " ").trim();

const isStateLikeSegment = (value) => /^[A-Za-z]{2}(?:\s*\d{5}(?:-\d{4})?)?$/.test(value);
const segmentHasDigit = (value) => /\d/.test(value);
export const looksLikeCityName = (value) =>
	Boolean(value) && /[A-Za-z]/.test(value) && !segmentHasDigit(value) && !isStateLikeSegment(value);

const cleanState = (value = "") => {
	const upper = normalizeWhitespace(value).toUpperCase();
	return /^[A-Z]{2}$/.test(upper) ? upper : "";
};

const parseCombinedStateZip = (segment = "") => {
	const match = normalizeWhitespace(segment).match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
	if (!match) {
		return { state: "", zip: "" };
	}
	return { state: cleanState(match[1]), zip: match[2] };
};

export const parseAddressParts = (address) => {
	const defaults = {
		street: "",
		city: "",
		state: "",
		postalCode: "",
		country: ""
	};

	if (!address) {
		return defaults;
	}

	let addressValue = address;
	if (Array.isArray(address)) {
		addressValue = address.filter(Boolean).join(", ");
	}

	if (typeof addressValue !== "string") {
		return defaults;
	}

	const normalizedAddress = normalizeWhitespace(addressValue.replace(/\n+/g, ","));
	let segments = normalizedAddress
		.split(",")
		.map((segment) => normalizeWhitespace(segment))
		.filter(Boolean);

	if (!segments.length) {
		return defaults;
	}

	// Drop country if present
	const lastSegment = segments[segments.length - 1].toUpperCase();
	if (lastSegment === "USA" || lastSegment === "US" || lastSegment === "U.S.A" || lastSegment === "U.S.") {
		segments.pop();
	}

	let street = "";
	let city = "";
	let state = "";
	let postalCode = "";

	// State + zip may be in one segment (e.g., "MI 49686")
	if (segments.length) {
		const combined = parseCombinedStateZip(segments[segments.length - 1]);
		if (combined.state && combined.zip) {
			postalCode = combined.zip;
			state = combined.state;
			segments.pop();
		}
	}

	// If no combined, look for zip then state in separate segments
	if (!postalCode && segments.length) {
		const candidateZip = segments[segments.length - 1];
		if (/^\d{5}(?:-\d{4})?$/.test(candidateZip)) {
			postalCode = candidateZip;
			segments.pop();
		}
	}

	if (!state && segments.length) {
		state = cleanState(segments[segments.length - 1]);
		if (state) {
			segments.pop();
		} else {
			state = "";
		}
	}

	if (segments.length) {
		city = segments.pop();
	}

	if (segments.length) {
		street = segments.join(", ");
	}

	return {
		street,
		city,
		state,
		postalCode,
		country: ""
	};
};

export const extractCityFromAddress = (address) => {
	if (!address) {
		return "";
	}

	if (Array.isArray(address)) {
		// Expected structure: [street, city, state, zip, country]
		const candidate = normalizeWhitespace(address[1] || "");
		if (looksLikeCityName(candidate)) {
			return candidate;
		}
		// Fallback: scan remaining segments for something that looks like a city
		for (let index = 0; index < address.length; index += 1) {
			const value = normalizeWhitespace(address[index] || "");
			if (looksLikeCityName(value)) {
				return value;
			}
		}
		return "";
	}

	if (typeof address !== "string") {
		return "";
	}

	const segments = normalizeWhitespace(address.replace(/\n+/g, ","))
		.split(",")
		.map((segment) => normalizeWhitespace(segment))
		.filter(Boolean);

	// Look for the first segment that resembles a city: contains letters, not state/zip, no digits.
	for (const segment of segments) {
		if (looksLikeCityName(segment)) {
			return segment;
		}
	}

	return "";
};

export const splitStreetLine = (street = "") => {
	const value = normalizeWhitespace(street);
	if (!value) {
		return { number: "", street: "" };
	}

	const match = value.match(/^(\d+[A-Za-z]?)(?:\s+)(.*)$/);
	if (match) {
		return { number: match[1], street: match[2] || "" };
	}

	return { number: "", street: value };
};
